import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Zap, Crown, Star, ArrowLeft } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../utils/config';
import ThemeToggle from './ThemeToggle';
import logo from '../assets/logo.svg';

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  documents_limit: number;
  ai_queries_limit: number;
  claude_model: string;
  features: string[];
}

interface Subscription {
  plan_type: string;
  status: string;
  is_trial: boolean;
  trial_end?: string;
  documents_used: number;
  documents_limit: number;
  ai_queries_used: number;
  ai_queries_limit: number;
  invoices_created: number;
  invoices_created_limit: number;
  invoices_edited: number;
  invoices_edited_limit: number;
  invoice_downloads: number;
  invoice_downloads_limit: number;
  claude_model: string;
  features: string[];
  current_period_end?: string;
  can_upgrade: boolean;
}

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string>('');

  const isLoggedIn = !!user;

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
    }
    fetchPlans();
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await apiClient.get(config.endpoints.subscription.status);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get(config.endpoints.subscription.plans);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load pricing plans');
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    setError('');
    try {
      await apiClient.post(config.endpoints.subscription.startTrial);
      await fetchSubscriptionStatus();
      // '/' is the public marketing landing page. Starting a trial should drop
      // the user into the product they just signed up to use.
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error starting trial:', error);
      setError(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId: string) => {
    if (!user) {
      // Redirect to multi-step registration for unauthenticated users
      navigate('/register');
      return;
    }

    if (planId === 'discovery') {
      await handleStartTrial();
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await apiClient.post(config.endpoints.subscription.createCheckout, {
        plan: planId
      });
      window.location.href = response.data.checkout_url;
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      setError(error.response?.data?.detail || 'Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const getModelBadge = (model: string) => {
    switch (model) {
      case 'claude-3-haiku-20240307':       // legacy
      case 'claude-3-5-haiku-20241022':     // legacy
      case 'claude-haiku-4-5':              // current basic tier
        return { text: 'Basic AI', color: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30' };
      case 'claude-sonnet-4-20250514':      // legacy
      case 'claude-sonnet-4-6':             // current advanced tier
      case 'claude-opus-4-8':               // current premium tier
        return { text: 'Advanced AI', color: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-500/30' };
      default:
        return { text: 'AI Powered', color: 'bg-stone-100 dark:bg-gray-500/20 text-stone-700 dark:text-gray-300 border-stone-200 dark:border-gray-500/30' };
    }
  };

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'discovery':
        return <Sparkles className="w-8 h-8" />;
      case 'essential':
        return <Zap className="w-8 h-8" />;
      case 'advanced':
        return <Crown className="w-8 h-8" />;
      default:
        return <Star className="w-8 h-8" />;
    }
  };

  const isCurrentPlan = (planId: string) => {
    return subscription?.plan_type === planId;
  };

  const canSelectPlan = (planId: string) => {
    if (!user) {
      return true; // Allow non-authenticated users to see all plans as selectable
    }
    if (planId === 'discovery') {
      return !subscription || (subscription.plan_type === 'none');
    }
    return !isCurrentPlan(planId);
  };

  if (error && plans.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-8">
          <h2 className="text-xl font-semibold text-red-700 dark:text-red-300 mb-2">Error Loading Plans</h2>
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-500/20 hover:bg-red-200 dark:hover:bg-red-500/30 text-red-700 dark:text-red-300 rounded-lg transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-coral-50 to-stone-100 dark:from-background dark:via-background dark:to-background">
      <nav className="w-full bg-white/80 dark:bg-black/30 backdrop-blur-md border-b border-stone-200 dark:border-coral-500/20">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img
                src={logo}
                alt="ViewPoint Logo"
                className="w-8 h-8 flex-shrink-0"
              />
              <span className="text-xl font-bold text-stone-900 dark:text-white font-display tracking-tight">ViewPoint</span>
            </div>
            <div className="flex items-center space-x-4">
              <ThemeToggle />
              <button
                onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}
                className="flex items-center space-x-2 text-white dark:text-white bg-coral-600 hover:bg-coral-700 dark:bg-coral-600 dark:hover:bg-coral-700 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm hover:shadow-md whitespace-nowrap"
              >
                <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                <span>{isLoggedIn ? 'Back to App' : 'Back to Login'}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-stone-900 dark:text-white mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-stone-700 dark:text-foreground-muted mb-2">
            Start with our free Discovery trial, upgrade anytime
          </p>
        {user && subscription && subscription.plan_type !== 'none' && (
          <div className="inline-flex items-center px-4 py-2 bg-coral-100 dark:bg-coral-500/20 border border-coral-200 dark:border-coral-500/30 rounded-full">
            <span className="text-sm text-stone-700 dark:text-foreground-muted">
              Current plan: <span className="font-semibold capitalize">{subscription.plan_type}</span>
              {subscription.is_trial && subscription.trial_end && (
                <span className="ml-2 text-xs">
                  (Trial ends {new Date(subscription.trial_end).toLocaleDateString()})
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
          <p className="text-red-600 dark:text-red-300 text-center">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => {
          const modelBadge = getModelBadge(plan.claude_model);
          const isPopular = plan.id === 'advanced';
          const isCurrent = isCurrentPlan(plan.id);

          return (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-black/30 backdrop-blur-sm rounded-xl p-6 border transition-all hover:shadow-lg ${
                isPopular
                  ? 'border-coral-400 dark:border-coral-400 shadow-2xl shadow-coral-500/20 transform scale-105'
                  : 'border-stone-200 dark:border-coral-500/20'
              } ${user && isCurrent ? 'ring-2 ring-coral-400' : ''}`}
            >
              {isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-coral-500 text-white text-xs font-semibold rounded-full">
                  MOST POPULAR
                </div>
              )}

              {user && isCurrent && (
                <div className="absolute -top-3 right-4 px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                  CURRENT
                </div>
              )}

              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4 ${
                  plan.id === 'advanced' ? 'bg-purple-100 dark:bg-purple-500/20' : 'bg-coral-100 dark:bg-coral-500/20'
                }`}>
                  <div className={plan.id === 'advanced' ? 'text-purple-600 dark:text-purple-400' : 'text-coral-600 dark:text-coral-400'}>{getPlanIcon(plan.id)}</div>
                </div>
                <h3 className="text-xl font-semibold text-stone-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-stone-600 dark:text-foreground-muted mb-4">{plan.description}</p>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-coral-600 dark:text-foreground-muted">
                    €{plan.price}
                  </span>
                  <span className="text-stone-500 dark:text-foreground-muted text-sm ml-2">
                    {plan.price === 0 ? '14 days' : 'per month'}
                  </span>
                </div>
                {plan.id !== 'discovery' && (
                  <div className={`inline-flex items-center px-3 py-1 border rounded-full ${modelBadge.color}`}>
                    <span className="text-xs">{modelBadge.text}</span>
                  </div>
                )}
              </div>

              {/* Usage display for current plan */}
              {user && isCurrent && subscription && (
                <div className="mb-6 p-3 bg-coral-50 dark:bg-coral-500/10 border border-coral-200 dark:border-coral-500/20 rounded-lg">
                  <h4 className="text-sm font-semibold text-stone-800 dark:text-foreground-muted mb-2">Your Usage</h4>
                  <div className="space-y-2 text-xs">
                    {[
                      { label: 'Documents', used: subscription.documents_used, limit: subscription.documents_limit },
                      { label: 'AI Queries', used: subscription.ai_queries_used, limit: subscription.ai_queries_limit },
                      { label: 'Invoices Created', used: subscription.invoices_created, limit: subscription.invoices_created_limit },
                      { label: 'Invoices Edited', used: subscription.invoices_edited, limit: subscription.invoices_edited_limit },
                      { label: 'PDF Downloads', used: subscription.invoice_downloads, limit: subscription.invoice_downloads_limit },
                    ].map(({ label, used, limit }) => (
                      <div key={label} className="flex justify-between text-stone-700 dark:text-foreground-secondary">
                        <span>{label}:</span>
                        <span className="tabular-nums">{used}/{limit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 dark:text-green-400 shrink-0 mt-0.5" />
                    <span className="text-sm text-stone-700 dark:text-foreground-secondary">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.id)}
                disabled={loading || (user && isCurrent) || !canSelectPlan(plan.id)}
                className={`w-full py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isPopular
                    ? 'bg-coral-500 hover:bg-coral-600 dark:bg-coral-500 dark:hover:bg-coral-600 text-white disabled:bg-coral-500/50'
                    : 'bg-coral-100 dark:bg-coral-500/20 hover:bg-coral-200 dark:hover:bg-coral-500/30 text-coral-700 dark:text-foreground-muted border border-coral-200 dark:border-coral-500/30 disabled:bg-coral-50 dark:disabled:bg-coral-500/10'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (user && isCurrent) ? (
                  'Current Plan'
                ) : !user ? (
                  'Sign Up & Get Started'
                ) : plan.id === 'discovery' ? (
                  'Start Free Trial'
                ) : (
                  `Get ${plan.name}`
                )}
              </button>
            </div>
          );
        })}
      </div>


      {/* FAQ or Support Section */}
      <div className="mt-16 text-center">
        <h2 className="text-2xl font-semibold text-stone-900 dark:text-white mb-4">
          Need Help Choosing?
        </h2>
        <p className="text-stone-700 dark:text-foreground-muted mb-6">
          Start with our free Discovery trial to try document extraction, invoicing
          and expenses, then choose the plan that best fits your needs.
          KSeF e-invoicing is available on Advanced.
        </p>
        <div className="flex justify-center gap-4">
          {user ? (
            <button
              onClick={() => navigate('/contact')}
              className="px-6 py-2 bg-coral-100 dark:bg-coral-500/20 hover:bg-coral-200 dark:hover:bg-coral-500/30 text-coral-700 dark:text-foreground-muted border border-coral-200 dark:border-coral-500/30 rounded-lg transition-colors"
            >
              Contact Support
            </button>
          ) : (
            /* /contact is behind ProtectedRoute; on this public page it bounced
               logged-out visitors straight to the login screen. */
            <a
              href="mailto:info@vpoint.app"
              className="px-6 py-2 bg-coral-100 dark:bg-coral-500/20 hover:bg-coral-200 dark:hover:bg-coral-500/30 text-coral-700 dark:text-foreground-muted border border-coral-200 dark:border-coral-500/30 rounded-lg transition-colors"
            >
              Contact Support
            </a>
          )}
          {!user ? (
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-coral-500 hover:bg-coral-600 dark:bg-coral-500 dark:hover:bg-coral-600 text-white rounded-lg transition-colors"
            >
              Sign Up for Free Trial
            </button>
          ) : !subscription && (
            <button
              onClick={handleStartTrial}
              disabled={loading}
              className="px-6 py-2 bg-coral-500 hover:bg-coral-600 dark:bg-coral-500 dark:hover:bg-coral-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Starting...' : 'Start Free Trial'}
            </button>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default PricingPage;